import { fetchV2WithAuth } from './apiAuth';
import type { ChatType } from '../components/chat/types';

/**
 * Starting a support chat: `POST /v2/chat/start` on FranchiseApi (WIPE-1408).
 *
 * This used to be an AgencyOS lambda on an API Gateway with no authorizer and no API key, so anyone
 * on the internet could open a contact in a live hotline queue. And the request decided almost
 * everything about it: the contact flow ARN, the queue, the routing team, and the customer's name,
 * email, branch number and agency name. A caller picked any flow in the instance and introduced
 * itself to the answering agent as anyone at any agency.
 *
 * So the payload here is deliberately thin. All this sends is which hotline to reach and the intake
 * answers the user typed. Who is asking, which branch they belong to, which team answers and which
 * flow the contact enters are resolved server-side from the verified token and the API's own
 * configuration — none of them is a parameter any more, and none of them belongs in this file.
 */
const API_BASE_URL = import.meta.env.VITE_FRANCHISE_API_URL;

/** The intake questions the pre-chat form asks. Every field is optional; the backend caps the sizes. */
export interface ChatIntake {
  insuredName?: string;
  lob?: string;
  lookupCode?: string;
  helpDescription?: string;
  contactNumber?: string;
  carrierName?: string;
  carrierUsername?: string;
}

/** Participant credentials for the Connect ChatJS client (see chat/sessionFactory). */
export interface ChatSession {
  contactId: string;
  participantId: string;
  participantToken: string;
}

class ConnectChatClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL || '';
  }

  async startChat(chatType: ChatType, intake: ChatIntake = {}): Promise<ChatSession> {
    if (!this.baseUrl) throw new Error('Chat is not configured (VITE_FRANCHISE_API_URL).');

    const response = await fetchV2WithAuth(`${this.baseUrl}/v2/chat/start`, {
      method: 'POST',
      body: JSON.stringify({ chat_type: chatType, intake: toIntakePayload(intake) }),
    });

    if (!response.ok) throw new Error(await errorMessage(response));

    const session = await response.json();
    return {
      contactId: session.contact_id,
      participantId: session.participant_id,
      participantToken: session.participant_token,
    };
  }

  isConfigured(): boolean {
    return !!this.baseUrl;
  }
}

// Only what the user actually answered. An empty string reads on the agent's screen as a question
// that was asked and left blank, which is not the same as one that was never asked.
function toIntakePayload(intake: ChatIntake): Record<string, string> {
  const fields: Array<[keyof ChatIntake, string]> = [
    ['insuredName', 'insured_name'],
    ['lob', 'lob'],
    ['lookupCode', 'lookup_code'],
    ['helpDescription', 'help_description'],
    ['contactNumber', 'contact_number'],
    ['carrierName', 'carrier_name'],
    ['carrierUsername', 'carrier_username'],
  ];
  const payload: Record<string, string> = {};
  for (const [field, key] of fields) {
    const value = intake[field]?.trim();
    if (value) payload[key] = value;
  }
  return payload;
}

// The v2 error contract is { error } or { errors: { general?, fields? } } from request validation.
// A 503 here means the environment has no Connect instance configured, which is the expected
// non-prod state rather than an outage.
async function errorMessage(response: Response): Promise<string> {
  const fallback = `Failed to start the chat (HTTP ${response.status})`;
  try {
    const body = await response.json();
    const errors = body?.errors;
    if (errors && typeof errors === 'object') {
      const fields: Record<string, string> = errors.fields ?? {};
      return errors.general ?? Object.values(fields)[0] ?? fallback;
    }
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

export const connectChat = new ConnectChatClient();
